<template>
  <div v-hotkey="shortcuts" class="cancel-button">
    <BaseButton
      kind="auxiliary"
      :text="cancelEventTitle"
      :tint="tint"
      :is-expanded="isExpanded"
      @click="queryCancel"
    >
      <template #icon>
        <component :is="cancelEventIcon" />
      </template>
    </BaseButton>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, toRefs } from "vue";

import { FullEvent } from "@ctrl/controller-rpc";

import { eventActions } from "@/components/actions/actions";
import BaseButton from "@/components/base/BaseButton.vue";
import { Tint } from "@/components/base/types";
import CloseIcon from "@/components/icons/CloseIcon.vue";
import * as inject from "@/components/inject";
import { assignShortcuts, Shortcuts } from "@/components/shortcuts";
import { Router } from "@/types";
import { navigateToAgenda } from "@/utils/navigate";

export default defineComponent({
  components: {
    BaseButton,
    CloseIcon,
  },
  props: {
    event: { type: Object as PropType<FullEvent>, required: true },
    isExpanded: { type: Boolean, required: false, default: false },
    navigateAfterDelete: { type: Boolean, default: false },
    tint: { type: String as PropType<Tint>, default: "none" },
  },
  emits: {
    flushDocument: () => true,
  },
  setup(props, { emit }) {
    const { event, navigateAfterDelete } = toRefs(props);
    const controller = inject.controller();
    const logger = inject.logger();
    const profile = inject.profile();
    const multipleEventsModal = inject.multipleEventsModal();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const router = inject.router();
    const simpleModal = inject.simpleModal();
    const routerIfNavigate = computed<Router | undefined>(() =>
      navigateAfterDelete.value ? router : undefined
    );
    const cancelEventTitle = eventActions.delete.text;
    const cancelEventIcon = eventActions.delete.icon;
    const queryCancel = () => {
      eventActions.delete.action(
        {
          type: "event",
          value: event.value,
        },
        {
          controller,
          logger,
          profile,
          simpleModal,
          multipleEventsModal,
        },
        {
          beforeAction: async () => {
            emit("flushDocument");
            if (routerIfNavigate.value)
              navigateToAgenda(router, event.value.start);
          },
        }
      );
    };
    const shortcuts = computed<Shortcuts>(() => {
      return {
        ...assignShortcuts(
          { isModalShown, isInputFocused },
          eventActions.delete.shortcuts,
          queryCancel
        ),
      };
    });
    return { cancelEventTitle, cancelEventIcon, queryCancel, shortcuts };
  },
});
</script>
